import { useState, useEffect, useCallback, useRef } from "react";
import { Search, Download, InfoCircle } from "iconoir-react";
import "@knowtex-ai/components/style.css";
import { Badge, Button, Card, FieldText, FieldCheckbox, PageHeader, LoadingAnimation, Popover, Tooltip } from "@knowtex-ai/components";
import { T, S, fmtDate } from "./theme";
import { calendarDaysSince } from "./outreachDates";
import {
  fetchOutreachCandidates,
  fetchOutreachExport,
} from "./api";
import OutreachDetail from "./OutreachDetail";
import { downloadOutreachXlsx } from "./outreachExport";
import { PRIORITY_TIERS, UNCLASSIFIED_TIER, UNCLASSIFIED_META, REMOVED_TIER, REMOVED_META, DEFAULT_TIER_KEYS, outreachStatusMetaFor } from "./outreachPriority";
import { parseOutreachHash } from "./outreachHash";

const archiveSubtext = (r) => {
  const reason = String(r.reason_for_archive || "").toLowerCase();
  if (reason === "launched" || reason === "recorded") return "Launched";
  if (reason === "out_of_attempts") return "Out of attempts";
  if (!r.removed_at) return r.reason_for_outreach;
  return r.reason_for_archive ? `Removed: ${r.reason_for_archive}` : "Removed";
};

const PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Table column headers. last_note_date gets a tooltip since it's easy to
// confuse with "last outreach attempt" — it's actually clinical activity
// (last recorded audio note), independent of any outreach we've done.
const COLUMNS = [
  { key: "provider", label: "Provider" },
  { key: "station", label: "Station / VISN" },
  { key: "date_activated", label: "Date Activated" },
  { key: "days_idle", label: "Days Idle" },
  { key: "attempts", label: "Attempts Used" },
  { key: "last_attempt_at", label: "Days Since Last Attempt", sortable: true },
  {
    key: "last_note_date",
    label: "Last Appointment",
    tooltip: "The most recent date this provider has an audio recorded.",
  },
  { key: "priority", label: "Priority" },
];

const DEFAULT_SORT = { key: "priority", dir: "asc" };

const ATTEMPT_OPTIONS = [0, 1, 2, 3];

const priorityValuesFor = (tiers) =>
  [...tiers].map((t) => (t === UNCLASSIFIED_TIER ? "null" : t === REMOVED_TIER ? "removed" : t));

const attemptValuesFor = (selected) =>
  selected.size < ATTEMPT_OPTIONS.length ? ATTEMPT_OPTIONS.filter((n) => selected.has(n)) : undefined;

export default function Outreach({ showToast }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Parsed once on mount: a plain #outreach/:id link opens straight to the
  // detail view, while a filtered link like
  // #outreach?priority=null&reason=launched (the Slack launch digest's
  // overflow link) opens the Archived-only list pre-filtered to launches.
  const initialHash = parseOutreachHash(window.location.hash);
  // Default: active tiers only — Archived is opt-in, unless the hash asked
  // for the archived list directly.
  const [selectedTiers, setSelectedTiers] = useState(() =>
    initialHash.archivedOnly ? new Set([UNCLASSIFIED_TIER]) : new Set(DEFAULT_TIER_KEYS)
  );
  const [selectedDoctorUserId, setSelectedDoctorUserId] = useState(() => initialHash.doctorUserId);
  const [archiveReason, setArchiveReason] = useState(() => initialHash.archiveReason);
  const [selectedAttempts, setSelectedAttempts] = useState(() => new Set(ATTEMPT_OPTIONS));
  const [page, setPage] = useState(1);
  // Default stays priority (page subtitle). last_attempt_at is the only
  // column staff can click to reorder the paginated list.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // Debounced value drives the fetch so typing doesn't fire a request per keystroke.
  const [queryText, setQueryText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(queryText.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [queryText]);

  const latestLoadRef = useRef(0);

  const load = useCallback(async () => {
    const loadId = ++latestLoadRef.current;
    // The API treats an empty list as "no filter", so skip the request.
    if (selectedTiers.size === 0 || selectedAttempts.size === 0) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchOutreachCandidates({
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
        search: debouncedSearch || undefined,
        priority: priorityValuesFor(selectedTiers),
        attempts: attemptValuesFor(selectedAttempts),
        reason: archiveReason || undefined,
        ...(sort.key === "last_attempt_at" ? { sort: sort.key, dir: sort.dir } : {}),
      });
      if (loadId !== latestLoadRef.current) return;
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (err) {
      if (loadId !== latestLoadRef.current) return;
      console.error("Failed to fetch outreach candidates:", err);
      showToast?.("Failed to load outreach candidates");
      setRows([]);
      setTotal(0);
    } finally {
      if (loadId === latestLoadRef.current) setLoading(false);
    }
  }, [page, debouncedSearch, selectedTiers, selectedAttempts, sort, archiveReason, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Any manual checkbox change means the user is picking their own filter —
  // drop the hash-driven reason filter so the list isn't stuck showing only
  // launches (e.g. after unchecking Archived and rechecking it).
  const toggleTier = (tier) => {
    setSelectedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier);
      else next.add(tier);
      return next;
    });
    setArchiveReason(null);
    setPage(1);
  };

  const toggleAttempt = (count) => {
    setSelectedAttempts((prev) => {
      const next = new Set(prev);
      if (next.has(count)) next.delete(count);
      else next.add(count);
      return next;
    });
    setPage(1);
  };

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      // First click: oldest attempt first = most days since (who is due next).
      return { key, dir: "asc" };
    });
    setPage(1);
  };

  // Exports every row matching the current search + tier selection, not just
  // the visible page. Bails on an empty selection because the API reads a
  // missing priority param as "no filter".
  const handleExport = async () => {
    if (exporting || selectedTiers.size === 0 || selectedAttempts.size === 0) return;
    setExporting(true);
    try {
      const res = await fetchOutreachExport({
        search: debouncedSearch || undefined,
        priority: priorityValuesFor(selectedTiers),
        attempts: attemptValuesFor(selectedAttempts),
        reason: archiveReason || undefined,
        ...(sort.key === "last_attempt_at" ? { sort: sort.key, dir: sort.dir } : {}),
      });
      const exportRows = res.rows || [];
      if (exportRows.length === 0) {
        showToast?.("No records to export for the current filters");
        return;
      }
      await downloadOutreachXlsx(exportRows);
    } catch (err) {
      console.error("Failed to export outreach candidates:", err);
      showToast?.("Failed to export outreach candidates");
    } finally {
      setExporting(false);
    }
  };

  const openDetail = (doctorUserId) => {
    setSelectedDoctorUserId(doctorUserId);
    window.location.hash = `outreach/${doctorUserId}`;
  };

  const handleBack = () => {
    setSelectedDoctorUserId(null);
    window.location.hash = "outreach";
    // Refetch so a just-logged attempt's count isn't stale on return.
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  if (selectedDoctorUserId) {
    return <OutreachDetail doctorUserId={selectedDoctorUserId} onBack={handleBack} showToast={showToast} />;
  }

  return (
    <>
      <PageHeader
        title="Outreach"
        subtitle="Providers flagged for follow-up outreach, ranked by priority (1 = most urgent)."
      />

      {/* Search bar */}
      {/* Flowbite's global Tailwind "forms" reset (bundled site-wide via
          public/index.html) puts a blue focus ring on every plain
          input[type=text] at the same CSS specificity as the design
          system's own focus reset, so it wins the tie and bleeds through
          here. Scope an override to this field rather than touching the
          global stylesheet. */}
      <style>{`
        .outreach-search-field input:focus {
          outline: none;
          box-shadow: none;
          border-color: #1b192e;
        }
        .outreach-candidates-table th,
        .outreach-candidates-table td {
          overflow-wrap: break-word;
        }
      `}</style>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <div className="outreach-search-field" style={{ flex: "1 1 320px", minWidth: 260 }}>
          <FieldText
            icon={Search}
            placeholder="Search provider, station ID or name, specialty, or attempt by..."
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            size="small"
            aria-label="Search outreach candidates"
          />
        </div>
        <Popover
          side="bottom"
          content={
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 4 }}>
              {ATTEMPT_OPTIONS.map((count) => (
                <FieldCheckbox
                  key={count}
                  checked={selectedAttempts.has(count)}
                  onCheckedChange={() => toggleAttempt(count)}
                  size="small"
                >
                  {String(count)}
                </FieldCheckbox>
              ))}
            </div>
          }
        >
          <Button variant="secondary" size="small" aria-label="Filter by attempts used">
            {`Attempts Used: ${
              selectedAttempts.size === ATTEMPT_OPTIONS.length
                ? "All"
                : selectedAttempts.size === 0
                  ? "None"
                  : attemptValuesFor(selectedAttempts).join(", ")
            }`}
          </Button>
        </Popover>
        <Button
          variant="secondary"
          size="small"
          icon={Download}
          loading={exporting}
          disabled={exporting || loading || total === 0}
          onClick={handleExport}
        >
          {exporting ? "Exporting..." : "Download XLSX"}
        </Button>
      </div>

      {/* Priority filter */}
      <div style={{ display: "flex", gap: 16, marginBottom: archiveReason === "launched" ? 8 : 20, flexWrap: "wrap" }}>
        {[
          ...PRIORITY_TIERS,
          { tier: UNCLASSIFIED_TIER, label: UNCLASSIFIED_META.label, badge: UNCLASSIFIED_META.badge },
          { tier: REMOVED_TIER, label: REMOVED_META.label, badge: REMOVED_META.badge },
        ].map(({ tier, label, badge }) => (
          <FieldCheckbox
            key={tier}
            checked={selectedTiers.has(tier)}
            onCheckedChange={() => toggleTier(tier)}
            size="small"
          >
            <Badge variant={badge}>{label}</Badge>
          </FieldCheckbox>
        ))}
      </div>
      {archiveReason === "launched" && (
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>
          Showing archived with reason Launched or Recorded
        </div>
      )}

      <Card variant="elevated" size="small">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Outreach Candidates</span>
          <span style={{ fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
            {loading ? <><LoadingAnimation size={14} /> Loading...</> : `${total} total`}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
        <table className="outreach-candidates-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                if (col.sortable) {
                  const active = sort.key === col.key;
                  return (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                      style={{ ...S.th, color: active ? T.accent : T.textDim, cursor: "pointer", userSelect: "none" }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ whiteSpace: "normal", lineHeight: 1.25 }}>{col.label}</span>
                        <span style={{ fontSize: 9, opacity: active ? 1 : 0.3, flexShrink: 0 }}>
                          {active && sort.dir === "asc" ? "▲" : "▼"}
                        </span>
                      </span>
                    </th>
                  );
                }
                return (
                  <th key={col.key} style={S.th}>
                    {col.tooltip ? (
                      <Tooltip label={col.tooltip} delay={100}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}>
                          {col.label}
                          <InfoCircle width={13} height={13} style={{ color: T.textMuted, pointerEvents: "none" }} />
                        </span>
                      </Tooltip>
                    ) : col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const meta = outreachStatusMetaFor(r);
              const isTierLabel = r.removed_at == null && r.priority != null;
              const daysSinceAttempt = calendarDaysSince(r.last_attempt_at);
              return (
                <tr
                  key={r.provider_id}
                  onClick={() => openDetail(r.provider_id)}
                  style={{ transition: "background 0.1s", cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = T.surface2)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                >
                  <td style={S.td}>
                    <div>{r.provider_name}</div>
                    <div style={{ ...S.muted, fontSize: 12 }}>{r.email}</div>
                    {r.specialty && (
                      <div style={{ marginTop: 4 }}>
                        <Badge variant="default">{r.specialty}</Badge>
                      </div>
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={S.mono}>{r.station_id}</div>
                    <div style={{ ...S.muted, fontSize: 12 }}>{r.station_name}{r.visn != null ? ` · VISN ${r.visn}` : ""}</div>
                  </td>
                  <td style={S.td}>{fmtDate(r.date_activated)}</td>
                  <td style={S.td}>{r.days_idle ?? "—"}</td>
                  <td style={S.td}>{r.attempt_count ?? 0}</td>
                  <td style={S.td}>
                    {daysSinceAttempt == null ? "—" : (
                      <>
                        <div>{daysSinceAttempt}</div>
                        {r.last_attempt_by ? (
                          <div style={{ ...S.muted, fontSize: 12 }}>{r.last_attempt_by}</div>
                        ) : null}
                        <div style={{ ...S.muted, fontSize: 12 }}>{fmtDate(r.last_attempt_at)}</div>
                      </>
                    )}
                  </td>
                  <td style={S.td}>{fmtDate(r.last_note_date)}</td>
                  <td style={S.td}>
                    {meta ? <Badge variant={meta.badge}>{isTierLabel ? `Tier ${r.priority}` : meta.label}</Badge> : r.priority}
                    {archiveSubtext(r) && (
                      <div style={{ ...S.muted, fontSize: 11, marginTop: 4 }}>{archiveSubtext(r)}</div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLUMNS.length} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No records to show</td></tr>
            )}
          </tbody>
        </table>
        </div>
        {!loading && total > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 12, color: T.textMuted }}>
              Showing {(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, total)} of {total}
            </span>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Button variant="secondary" size="small" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>Page {currentPage} of {totalPages}</span>
              <Button variant="secondary" size="small" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
